import { Analytics } from '@vercel/analytics/react';
import { AppHeader } from '@/components/AppHeader';
import { BottomNavigation } from '@/components/BottomNavigation';
import { TopNavigation } from '@/components/TopNavigation';
import { AppFooter } from '@/components/AppFooter';
import { useAuth } from '@/contexts/AuthContext';
import { useResponsive } from '@/hooks/use-responsive';
import { useLanguage } from '@/hooks/useLanguage';
import { logError } from '@/utils/error-handling';
import React, { useCallback, useEffect, useState } from 'react';
import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import './styles/index.css';

const LoginPage = React.lazy(() => import('@/pages/Login'));
const RegisterPage = React.lazy(() => import('@/pages/Register'));
const ForgotPasswordPage = React.lazy(() => import('@/pages/ForgotPassword'));
const ResetPasswordPage = React.lazy(() => import('@/pages/ResetPassword'));

const PUBLIC_AUTH_ROUTES = ['/login', '/register', '/forgot-password', '/reset-password'];
const EventosPage = React.lazy(() => import('@/pages/Eventos'));
const PerfilPage = React.lazy(() => import('@/pages/Perfil'));
const ChatsPage = React.lazy(() => import('@/pages/ActivityHub'));
const QuienesSomosPage = React.lazy(() => import('@/pages/QuienesSomos'));
const AdministradorPage = React.lazy(() => import('@/pages/Administrador'));
const QRPage = React.lazy(() => import('@/pages/QR'));
const MeetingScanPage = React.lazy(() => import('@/pages/MeetingScan'));
const EventoDetallePage = React.lazy(() => import('@/pages/EventoDetalle'));
const NotFoundPage = React.lazy(() => import('@/pages/NotFound'));
const Step0PrivacyPolicy = React.lazy(() => import('@/pages/onboarding/Step0PrivacyPolicy'));
const Step1PersonalInfo = React.lazy(() => import('@/pages/onboarding/Step1PersonalInfo'));
const Step2WelcomeQuestion = React.lazy(() => import('@/pages/onboarding/Step2WelcomeQuestion'));
const Step7PWATutorial = React.lazy(() => import('@/pages/onboarding/Step7PWATutorial'));
const AjustesPage = React.lazy(() => import('@/pages/Ajustes'));
const VotacionPage = React.lazy(() => import('@/pages/Votacion'));
const RequireDesktop = React.lazy(() => import('@/components/guards/RequireDesktop').then(m => ({ default: m.RequireDesktop })));
const RequireSuperAdmin = React.lazy(() => import('@/components/guards/RequireSuperAdmin').then(m => ({ default: m.RequireSuperAdmin })));
const RequireOrgRole = React.lazy(() => import('@/components/organizer/RequireOrgRole').then(m => ({ default: m.RequireOrgRole })));
const OrganizerLayoutPage = React.lazy(() => import('@/pages/organizer/OrganizerLayout'));
const OrganizerDashboardPage = React.lazy(() => import('@/pages/organizer/Dashboard'));
const OrganizerEventsListPage = React.lazy(() => import('@/pages/organizer/EventsList'));
const OrganizerEventNewPage = React.lazy(() => import('@/pages/organizer/EventNew'));
const OrganizerEventEditPage = React.lazy(() => import('@/pages/organizer/EventEdit'));
const OrganizerAttendeesPage = React.lazy(() => import('@/pages/organizer/Attendees'));
const OrganizerNetworkingPage = React.lazy(() => import('@/pages/organizer/Networking'));
const OrganizerAnalyticsPage = React.lazy(() => import('@/pages/organizer/Analytics'));
const OrganizerNotificationsPage = React.lazy(() => import('@/pages/organizer/Notifications'));
const OrganizerSettingsPage = React.lazy(() => import('@/pages/organizer/Settings'));

function RootLayoutNav() {
  const { isAuthenticated, isLoading, getCurrentUserProfile } = useAuth();
  const { t } = useLanguage();
  const { isMobile } = useResponsive();
  const location = useLocation();
  const navigate = useNavigate();
  const [hasCheckedOnboarding, setHasCheckedOnboarding] = useState(false);

  const isOnboardingTestMode = location.search.includes('onboardingTest=1');

  const checkOnboardingStatus = useCallback(async () => {
    try {
      const userProfile = await getCurrentUserProfile();
      const hasCompletedOnboarding = userProfile?.hasCompletedOnboarding || false;

      setHasCheckedOnboarding(true);

      if (!hasCompletedOnboarding && !location.pathname.startsWith('/onboarding')) {
        navigate('/onboarding/Step0PrivacyPolicy', { replace: true });
      } else if (hasCompletedOnboarding && location.pathname.startsWith('/onboarding') && !isOnboardingTestMode) {
        navigate('/eventos', { replace: true });
      }
    } catch (error) {
      logError(error, 'checkOnboardingStatus', true);
      setHasCheckedOnboarding(true);
      if (location.pathname.startsWith('/onboarding')) {
        navigate('/eventos', { replace: true });
      }
    }
  }, [getCurrentUserProfile, location.pathname, location.search, isOnboardingTestMode, navigate]);

  const checkOnboardingAndRedirect = useCallback(async () => {
    try {
      const userProfile = await getCurrentUserProfile();
      const hasCompletedOnboarding = userProfile?.hasCompletedOnboarding || false;

      if (!hasCompletedOnboarding) {
        navigate('/onboarding/Step0PrivacyPolicy', { replace: true });
      } else {
        navigate('/eventos', { replace: true });
      }
    } catch (error) {
      logError(error, 'checkOnboardingAndRedirect', true);
      navigate('/eventos', { replace: true });
    }
  }, [getCurrentUserProfile, navigate]);

  useEffect(() => {
    if (isLoading) {
      return;
    }

    if (!isAuthenticated) {
      const isPublicRoute =
        PUBLIC_AUTH_ROUTES.includes(location.pathname) || location.pathname.startsWith('/onboarding');

      if (!isPublicRoute) {
        navigate('/login', { replace: true });
      }
      setHasCheckedOnboarding(false);
      return;
    }

    // Si está autenticado, verificar onboarding (no durante reset de contraseña)
    if (isAuthenticated && !hasCheckedOnboarding) {
      if (location.pathname !== '/reset-password') {
        checkOnboardingStatus();
      }
      return;
    }

    // Si está en ruta pública y autenticado, redirigir (excepto reset de contraseña)
    const isPublicRoute = PUBLIC_AUTH_ROUTES.includes(location.pathname);

    if (isAuthenticated && isPublicRoute && location.pathname !== '/reset-password') {
      checkOnboardingAndRedirect();
    }
  }, [isAuthenticated, isLoading, location.pathname, navigate, hasCheckedOnboarding, checkOnboardingStatus, checkOnboardingAndRedirect]);

  // Determinar si la ruta actual es pública (no necesita navegación)
  const isPublicRoute =
    PUBLIC_AUTH_ROUTES.includes(location.pathname) || location.pathname.startsWith('/onboarding');
  const showNavigation = isAuthenticated && !isPublicRoute;
  const isChatsRoute = location.pathname === '/chats';
  const isMobileDmConversationOpen =
    isMobile && isChatsRoute && Boolean(new URLSearchParams(location.search).get('user'));
  const isPanelRoute =
    location.pathname.startsWith('/administrador') || location.pathname.startsWith('/organizer');
  const desktopNavPadding = isPanelRoute ? '56px' : '80px';
  const showFooter = showNavigation && !isMobile && !isChatsRoute;

  const mobileBottomPadding = showNavigation && isMobile
    ? isMobileDmConversationOpen
      ? 'calc(var(--mobile-bottom-nav-bar) + env(safe-area-inset-bottom, 0px))'
      : `calc(var(--mobile-bottom-nav-bar) + var(--mobile-bottom-nav-float-gap) + var(--mobile-scroll-bottom-extra) + env(safe-area-inset-bottom, 0px))`
    : isChatsRoute
      ? '0px'
      : '60px';

  // Mostrar indicador de carga mientras se verifica la autenticación
  if (isLoading) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          backgroundColor: 'var(--bg-0)',
          color: 'var(--gray-text)',
          fontSize: '16px',
        }}
      >
        {t('common.loading')}
      </div>
    );
  }

  return (
    <>
      {/* Navegación superior para desktop/tablet */}
      {showNavigation && <TopNavigation />}

      {/* Header para móvil */}
      {showNavigation && isMobile && (
        <AppHeader />
      )}

      {/* Navegación inferior para móvil */}
      {showNavigation && isMobile && (
        <BottomNavigation />
      )}

      <div
        id="page-content-wrapper"
        className={showNavigation && isMobile ? 'app-shell-mobile' : undefined}
        style={{
          paddingTop: showNavigation
            ? (isMobile
              ? `calc(var(--mobile-header-bar) + env(safe-area-inset-top, 0px))`
              : desktopNavPadding)
            : `env(safe-area-inset-top, 0px)`,
          paddingBottom: showNavigation ? mobileBottomPadding : `env(safe-area-inset-bottom, 0px)`,
        }}
      >
        <React.Suspense
          fallback={
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100vh',
                backgroundColor: 'var(--bg-0)',
                color: 'var(--gray-text)',
                fontSize: '16px',
              }}
            >
              {t('common.loading')}
            </div>
          }
        >
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/onboarding/Step0PrivacyPolicy" element={<Step0PrivacyPolicy />} />
            <Route path="/onboarding/Step1PersonalInfo" element={<Step1PersonalInfo />} />
            <Route path="/onboarding/Step2WelcomeQuestion" element={<Step2WelcomeQuestion />} />
            <Route path="/onboarding/Step7PWATutorial" element={<Step7PWATutorial />} />
            <Route path="/eventos" element={<EventosPage />} />
            <Route path="/conexiones" element={<Navigate to="/chats" replace />} />
            <Route path="/perfil" element={<PerfilPage />} />
            <Route path="/chats" element={<ChatsPage />} />
            <Route path="/chats/scan-meeting" element={<MeetingScanPage />} />
            <Route path="/actividad" element={<Navigate to="/chats" replace />} />
            <Route path="/quienes-somos" element={<QuienesSomosPage />} />
            <Route path="/administrador" element={<RequireDesktop><RequireSuperAdmin><AdministradorPage /></RequireSuperAdmin></RequireDesktop>} />
            <Route path="/organizer" element={<RequireDesktop><RequireOrgRole><OrganizerLayoutPage /></RequireOrgRole></RequireDesktop>}>
              <Route index element={<Navigate to="/organizer/dashboard" replace />} />
              <Route path="dashboard" element={<OrganizerDashboardPage />} />
              <Route path="events" element={<OrganizerEventsListPage />} />
              <Route path="events/new" element={<OrganizerEventNewPage />} />
              <Route path="events/:id/edit" element={<OrganizerEventEditPage />} />
              <Route path="attendees" element={<OrganizerAttendeesPage />} />
              <Route path="networking" element={<OrganizerNetworkingPage />} />
              <Route path="analytics" element={<OrganizerAnalyticsPage />} />
              <Route path="notifications" element={<OrganizerNotificationsPage />} />
              <Route path="settings" element={<OrganizerSettingsPage />} />
            </Route>
            <Route path="/ajustes" element={<AjustesPage />} />
            <Route path="/votacion" element={<VotacionPage />} />
            <Route path="/evento/:id" element={<EventoDetallePage />} />
            <Route path="/qr" element={<QRPage />} />
            <Route path="/" element={<Navigate to="/eventos" replace />} />
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </React.Suspense>
      </div>

      {/* Footer con versión */}
      {showFooter && <AppFooter />}
    </>
  );
}

/** Shell de la app (rutas + nav). Providers y Router viven en main.tsx para HMR estable. */
export default function App() {
  return (
    <>
      <RootLayoutNav />
      <Analytics />
    </>
  );
}
